import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvSection,
	EvSpinner,
	IFilterDefinition,
	IFilterValues,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TFilterOptions
} from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';

import { DASHBOARD_DATE_RANGE_OPTIONS } from '../../constants/DashboardFiltersDefinitions';

import './DashboardFilters.scss';

interface DashboardFiltersProps {
	filters: IFilterOptions;
	onFilterChange: (filters: IFilterOptions) => void;
	isLoading?: boolean;
	filterDefinitions?: IFilterDefinition[];
	filterOptions?: Record<string, OptionType[]>;
}

export const DashboardFilters: FC<DashboardFiltersProps> = ({
	filters,
	onFilterChange,
	isLoading = false,
	filterDefinitions,
	filterOptions: customFilterOptions
}) => {
	const initialValues = useMemo((): IFilterValues => {
		return {
			text: {},
			select: filters as TFilterOptions
		};
	}, [filters]);

	const filterOptions = useMemo(() => {
		// Use custom filter options if provided, otherwise fall back to date range only
		if (customFilterOptions) {
			return customFilterOptions;
		}

		// Fallback to date range only if no custom options provided
		return {
			dateRange: DASHBOARD_DATE_RANGE_OPTIONS
		};
	}, [customFilterOptions]);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(flatFilters);
	};

	const handleReset = (): void => {
		onFilterChange({});
	};

	return (
		<EvSection
			ariaLabel="Dashboard mock filters"
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="dashboard-filters-mock-section"
			className="dashboard-filters-mock-section"
		>
			{!isLoading ? (
				<EvFilters
					className="dashboard-filters-mock"
					onApply={handleApply}
					onReset={handleReset}
					initialValues={initialValues}
					filterOptions={filterOptions}
					filterDefinitions={filterDefinitions}
					isFilterOptionsLoaded={!isLoading}
					isInitialValuesLoaded={!isLoading}
				/>
			) : (
				<EvSpinner />
			)}
		</EvSection>
	);
};
